import {
  inject,
  Injectable,
  Signal,
  signal,
  WritableSignal,
} from '@angular/core';
import { UserDataFacade } from '../../user-data/facade/user-data-facade';
import {
  catchError,
  EMPTY,
  last,
  map,
  Observable,
  of,
  switchMap,
  take,
  tap,
  withLatestFrom,
} from 'rxjs';
import {
  Address,
  BoelsBillingAddress,
  BoelsUser,
  BoelsUserTypeEnum,
  Cart,
  Country,
  Debtor,
  DebtorAccount,
  GlobalMessageType,
  PaymentDetails,
  PostalCode,
  StorageKey,
  TitleList,
  UserEnum,
} from '@boels/models';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import {
  CustomFormValidators,
  FormService,
  UserTypeService,
} from '@boels/helpers';
import { CartFacade } from '../../cart/facade/cart.facade';
import { CheckoutPaymentFacade } from '../../checkout/facade/checkout-payment.facade';
import { CultureFacade } from '../../cultures/facade/cultures.facade';
import { DeliveryFacade } from '../../delivery-mode/facade/delivery-facade.service';
import { UserProfileFacade } from '../../user-profile/facade/user-profile.facade';
import { toSignal } from '@angular/core/rxjs-interop';
import { GlobalMessageFacade } from '../../global-message/facade';

export interface PaymentFormState {
  registerdUser: Signal<boolean>;
  form: Signal<FormGroup>;
  debtorFormMetaData?: {
    debtor: Signal<Debtor>;
    debtorAccounts: Signal<Array<DebtorAccount>>;
  };
  billingFormMetaData?: {
    isTradeCustomer: WritableSignal<boolean>;
    isHouseNumberRequired: WritableSignal<boolean>;
    postalCodePlaceholder: WritableSignal<string>;
    postalCodeValidationMessage: WritableSignal<string>;
    postalCodePositionOfSpace: WritableSignal<number | undefined>;
  };
  billingFormExternalSources?: {
    titles$: Signal<TitleList>;
    countries$: Signal<{ countries: Country[] }>;
    culture$: Observable<string>;
  };
}

@Injectable()
export class CustomerDetailsService {
  //Dependency injections
  private userDataFacade = inject(UserDataFacade);
  private formService = inject(FormService);
  private userProfileFacade = inject(UserProfileFacade);
  private deliveryFacade = inject(DeliveryFacade);
  private cartFacade = inject(CartFacade);
  private checkoutPaymentFacade = inject(CheckoutPaymentFacade);
  private cultureService = inject(CultureFacade);
  private fb = inject(FormBuilder);
  private messageService = inject(GlobalMessageFacade);
  private userTypeService = inject(UserTypeService);

  initFormData(): Observable<PaymentFormState> {
    return this.userDataFacade.userData().pipe(
      map((user: BoelsUser) => {
        if (user.type === UserEnum.B2C_AUTHENTICATED)
          return this.debtorPaymentFormData(user);

        return this.billingPaymentFormData();
      }),
      catchError(() => {
        return EMPTY;
      }),
    );
  }

  setBillingFormMetaData(
    billingAddressFormData: PaymentFormState,
  ): Observable<PaymentFormState> {
    return this.setCustomerTypeInitials(billingAddressFormData);
  }

  patchDebtorFormIfAny(
    paymentFormData: PaymentFormState,
  ): Observable<PaymentFormState> {
    return this.patchDebtorForm(paymentFormData);
  }

  getFormItem<T extends FormControl | FormGroup>(
    form: FormGroup,
    key: string,
    nestedKey?: string,
  ): T {
    return this.formService.getFormItem(form, key, nestedKey) as T;
  }

  submitForm(paymentFormData: PaymentFormState): Observable<boolean> {
    if (paymentFormData.registerdUser())
      return this.submitDebtorForm(paymentFormData);
    return this.submitBillingForm(paymentFormData).pipe(
      tap(() => this.setTempUsertypeIfEmpty()),
    );
  }

  billingCustomerTypeChange(paymentFormData: PaymentFormState): void {
    const customerType = this.getFormItem(
      paymentFormData.form(),
      'individualControl',
    ).value;
    if (paymentFormData.billingFormMetaData) {
      paymentFormData.billingFormMetaData.isTradeCustomer.set(
        customerType === BoelsUserTypeEnum.BUSINESS,
      );
      this.formService.setFormValidators(
        paymentFormData.form(),
        paymentFormData.billingFormMetaData.isTradeCustomer(),
      );
      this.updateUserType(customerType as BoelsUserTypeEnum);
    }
  }

  debtorInfoText(debtor: Debtor | undefined): string {
    if (!debtor) return '';
    let value = `${debtor.firstName} ${debtor.lastName}`;
    if (debtor.contactInfo)
      value += ` (${
        debtor.contactInfo.find((info) => info.classification === 'email')
          ?.value
      })`;
    return value;
  }

  convertCartAndLogout(): void {
    this.cartFacade
      .convertCartToAnonymous()
      .pipe(
        withLatestFrom(this.cultureService.getActiveCulture()),
        take(1),
        map((result) => result[1]),
        tap((culture) => {
          this.cartFacade.setCheckoutAccount(UserEnum.ANONYMOUS);
          this.userDataFacade.requestLogout(`/${culture}/checkout-account`);
        }),
      )
      .subscribe();
  }

  displayError(error: string): void {
    console.log(error);
    this.messageService.add(
      { key: 'confirmation.error.text', scope: 'checkout' },
      GlobalMessageType.MSG_TYPE_ERROR,
    );
  }

  private debtorPaymentFormData(user: BoelsUser): PaymentFormState {
    return {
      registerdUser: signal(true),
      form: signal(this.debtorForm()),
      debtorFormMetaData: this.debtorFormMetaData(user),
    };
  }

  private debtorForm(): FormGroup {
    return this.fb.group({
      debtor: [null, Validators.required],
      reference: ['', Validators.required],
      phone: ['', Validators.required],
    });
  }

  private debtorFormMetaData(
    user: BoelsUser,
  ): PaymentFormState['debtorFormMetaData'] {
    return !user.debtor
      ? undefined
      : {
          debtor: signal(user.debtor),
          debtorAccounts: signal(
            user.debtor.accounts
              .filter((account) => account.addresses.some(Boolean))
              .sort((a, b) => a.name.localeCompare(b.name)),
          ),
        };
  }

  private billingPaymentFormData(): PaymentFormState {
    return {
      registerdUser: signal(false),
      form: signal(this.billingAddressForm()),
      billingFormMetaData: this.billingFormMetaDeta(),
      billingFormExternalSources: {
        countries$: toSignal(this.userProfileFacade.getCountries(), {
          initialValue: { countries: [] },
        }),
        titles$: toSignal(this.userProfileFacade.getTitles(), {
          initialValue: { titles: [] },
        }),
        culture$: this.cultureService.getActiveCulture(),
      },
    };
  }

  private billingAddressForm(): FormGroup {
    return this.fb.group({
      email: new FormControl<string>('', {
        validators: [Validators.required, CustomFormValidators.emailValidator],
      }),
      titleCode: ['', Validators.required],
      firstName: ['', Validators.required],
      lastName: ['', Validators.required],
      line1: ['', Validators.required],
      line2: ['', Validators.required],
      postalCode: ['', Validators.required],
      town: ['', Validators.required],
      country: this.fb.group({
        isocode: ['', Validators.required],
      }),
      phone: ['', Validators.required],
      tradeCustomer: this.fb.group({
        companyName: [''],
        debtorNumber: ['', CustomFormValidators.onlyAlphaNumerics],
        vatNumber: [''],
        chamberOfCommerceNumber: [''],
      }),
      individualControl: ['', Validators.required],
    });
  }

  private billingFormMetaDeta(): PaymentFormState['billingFormMetaData'] {
    return {
      isTradeCustomer: signal(false),
      isHouseNumberRequired: signal(false),
      postalCodePlaceholder: signal(''),
      postalCodeValidationMessage: signal(''),
      postalCodePositionOfSpace: signal(undefined),
    };
  }

  private patchDebtorForm(
    paymentFormData: PaymentFormState,
  ): Observable<PaymentFormState> {
    return this.cartFacade.getActiveCart().pipe(
      map((cart: Cart) => {
        const debtorNumber = cart.paymentInfo?.billingAddress?.debtorNumber;
        if (debtorNumber) {
          const debtor = paymentFormData.debtorFormMetaData
            ?.debtorAccounts()
            .find((account) => account.debtorNumber === debtorNumber);
          this.getFormItem(paymentFormData.form(), 'debtor').patchValue(debtor);
          this.getFormItem(paymentFormData.form(), 'reference').patchValue(
            cart.paymentInfo?.projectReference || '',
          );
          this.getFormItem(paymentFormData.form(), 'phone').patchValue(
            cart.paymentInfo?.billingAddress?.phone || '',
          );
        } else {
          const accounts = paymentFormData.debtorFormMetaData?.debtorAccounts();
          if (accounts && accounts.length === 1)
            this.getFormItem(paymentFormData.form(), 'debtor').patchValue(
              accounts[0],
            );
        }
        return paymentFormData;
      }),
      take(1),
    );
  }

  private setCustomerTypeInitials(
    billingPaymentFormData: PaymentFormState,
  ): Observable<PaymentFormState> {
    const radioControl = this.getFormItem(
      billingPaymentFormData.form(),
      'individualControl',
    );
    const temporaryType = this.userTypeService.getUserType(
      StorageKey.TEMP_USER_TYPE,
    );
    if (temporaryType) {
      radioControl.setValue(temporaryType);
    } else {
      radioControl.setValue(
        this.userTypeService.getUserType() || BoelsUserTypeEnum.BUSINESS,
      );
    }
    if (billingPaymentFormData.billingFormMetaData)
      billingPaymentFormData.billingFormMetaData.isTradeCustomer.set(
        radioControl.value !== BoelsUserTypeEnum.INDIVIDUAL,
      );

    return this.setCountryInitials(billingPaymentFormData);
  }

  private setTempUsertypeIfEmpty(): void {
    const temporaryType = this.userTypeService.getUserType(
      StorageKey.TEMP_USER_TYPE,
    );
    const userType = this.userTypeService.getUserType();
    if (!temporaryType && userType) {
      this.userTypeService.setUserType(userType, StorageKey.TEMP_USER_TYPE);
    }
  }

  private setCountryInitials(
    billingPaymentFormData: PaymentFormState,
  ): Observable<PaymentFormState> {
    if (billingPaymentFormData.billingFormExternalSources)
      this.formService.setCountryInitials(
        billingPaymentFormData.form(),
        this.userProfileFacade.getCountries(),
      );
    return this.setFormValidators(billingPaymentFormData);
  }

  private setFormValidators(
    billingPaymentFormData: PaymentFormState,
  ): Observable<PaymentFormState> {
    if (billingPaymentFormData.billingFormMetaData)
      this.formService.setFormValidators(
        billingPaymentFormData.form(),
        billingPaymentFormData.billingFormMetaData.isTradeCustomer(),
      );
    return this.patchbillingFormValues(billingPaymentFormData);
  }

  private patchbillingFormValues(
    billingPaymentFormData: PaymentFormState,
  ): Observable<PaymentFormState> {
    return this.cartFacade.getActiveCart().pipe(
      switchMap((cart: Cart) => {
        if (cart.paymentInfo?.billingAddress)
          billingPaymentFormData
            .form()
            .patchValue(cart.paymentInfo?.billingAddress);
        billingPaymentFormData.form().controls['tradeCustomer'].patchValue({
          companyName: cart.paymentInfo?.billingAddress?.companyName,
          debtorNumber: cart.paymentInfo?.billingAddress?.debtorNumber,
          vatNumber: cart.paymentInfo?.billingAddress?.vatNumber,
          chamberOfCommerceNumber:
            cart.paymentInfo?.billingAddress?.chamberOfCommerceNumber,
        });
        return this.setHouseNumberValidation(billingPaymentFormData);
      }),
    );
  }

  private setHouseNumberValidation(
    billingPaymentFormData: PaymentFormState,
  ): Observable<PaymentFormState> {
    if (billingPaymentFormData.billingFormExternalSources)
      this.formService.setHouseNumberValidation(
        billingPaymentFormData.form(),
        this.cultureService.getActiveCulture(),
      );
    const line2Control = this.getFormItem(
      billingPaymentFormData.form(),
      'line2',
    );

    if (billingPaymentFormData.billingFormMetaData)
      billingPaymentFormData.billingFormMetaData.isHouseNumberRequired.set(
        line2Control ? this.formService.isRequired(line2Control) : false,
      );

    return this.setPostalCodeValidation(billingPaymentFormData);
  }

  private setPostalCodeValidation(
    billingPaymentFormData: PaymentFormState,
  ): Observable<PaymentFormState> {
    if (!billingPaymentFormData.billingFormExternalSources) return EMPTY;
    return this.formService
      .setPostalCodeValidation(
        billingPaymentFormData.form(),
        this.cultureService.getActiveCulture(),
      )
      .pipe(
        tap((postalCode: PostalCode | null) => {
          if (postalCode && billingPaymentFormData.billingFormMetaData) {
            billingPaymentFormData.billingFormMetaData.postalCodePlaceholder.set(
              postalCode.postalCodePlaceholder,
            );
            billingPaymentFormData.billingFormMetaData.postalCodeValidationMessage.set(
              postalCode.postalCodeValidationMessage,
            );
            billingPaymentFormData.billingFormMetaData.postalCodePositionOfSpace.set(
              postalCode.postalCodePositionOfSpace,
            );
          }
        }),
        map(() => billingPaymentFormData),
      );
  }

  private updateUserType(value: BoelsUserTypeEnum): void {
    this.userTypeService.setUserType(value, StorageKey.TEMP_USER_TYPE);
  }

  private submitBillingForm(
    paymentFormData: PaymentFormState,
  ): Observable<boolean> {
    const address: BoelsBillingAddress =
      this.mapBillingAddress(paymentFormData);
    const paymentDetails: PaymentDetails = {
      billingAddress: address,
    };

    return this.checkoutPaymentFacade.setPaymentInfo(paymentDetails).pipe(
      tap((details: PaymentDetails) =>
        this.cartFacade.setPaymentDetails(details),
      ),
      withLatestFrom(this.cartFacade.getActiveCart()),
      map((result) => result[1]),
      switchMap((cart) => {
        return this.deliveryFacade
          .setShippingAddress(cart.deliveryAddress || address)
          .pipe(
            tap((address: Address) =>
              this.cartFacade.setDeliveryAddress(address),
            ),
            map(() => true),
          );
      }),
    );
  }

  private mapBillingAddress(
    paymentFormData: PaymentFormState,
  ): BoelsBillingAddress {
    const controlValue = (key: string, nestedKey?: string) =>
      this.getFormItem<FormControl>(paymentFormData.form(), key, nestedKey)
        .value;
    const postalCode = this.formService.formatPostalCode(
      controlValue('postalCode'),
      paymentFormData.billingFormMetaData?.postalCodePositionOfSpace() || 0,
    );

    return {
      companyName: controlValue('tradeCustomer', 'companyName'),
      debtorNumber: controlValue('tradeCustomer', 'debtorNumber'),
      vatNumber: controlValue('tradeCustomer', 'vatNumber'),
      chamberOfCommerceNumber: controlValue(
        'tradeCustomer',
        'chamberOfCommerceNumber',
      ),
      email: controlValue('email') as string,
      titleCode: controlValue('titleCode') as string,
      firstName: controlValue('firstName') as string,
      lastName: controlValue('lastName') as string,
      line1: controlValue('line1') as string,
      line2: controlValue('line2') as string,
      postalCode,
      town: controlValue('town') as string,
      country: {
        name: undefined,
        isocode: controlValue('country', 'isocode') as string,
      },
      phone: controlValue('phone').e164Number as string,
    };
  }

  private submitDebtorForm(
    paymentFormData: PaymentFormState,
  ): Observable<boolean> {
    return this.userProfileFacade.getCountries(true).pipe(
      switchMap((countries) => {
        const billingAddress = this.mapDebtorBillingAddress(
          paymentFormData,
          countries,
        );
        const paymentDetails: PaymentDetails = {
          billingAddress: billingAddress,
          projectReference: this.getFormItem<FormControl>(
            paymentFormData.form(),
            'reference',
          ).value as string,
        };
        return this.checkoutPaymentFacade.setPaymentInfo(paymentDetails).pipe(
          tap((details: PaymentDetails) =>
            this.cartFacade.setPaymentDetails(details),
          ),
          withLatestFrom(this.cartFacade.getActiveCart()),
          map((result) => result[1]),
          switchMap((cart) => {
            if (cart.deliveryAddress) return of(true);
            const defaultShippingAddress = { ...billingAddress };
            defaultShippingAddress.defaultAddress = true;
            return this.deliveryFacade
              .setShippingAddress(defaultShippingAddress)
              .pipe(
                last(),
                tap((address: Address) =>
                  this.cartFacade.setDeliveryAddress(address),
                ),
                map(() => true),
              );
          }),
        );
      }),
    );
  }

  private mapDebtorBillingAddress(
    paymentFormData: PaymentFormState,
    countries: { countries: Country[] },
  ): BoelsBillingAddress {
    const account: DebtorAccount = this.getFormItem<FormControl>(
      paymentFormData.form(),
      'debtor',
    ).value;
    const phone = this.getFormItem<FormControl>(paymentFormData.form(), 'phone')
      .value.e164Number as string;
    const billingAddress: BoelsBillingAddress = {
      debtorNumber: account.debtorNumber,
      companyName: account.name,
      debtorName: account.name,
      firstName: paymentFormData.debtorFormMetaData?.debtor().firstName,
      lastName: paymentFormData.debtorFormMetaData?.debtor().lastName,
      nameExtraInfo: account.nameExtraInfo,
    };

    if (account.addresses.length > 0) {
      const accountAddress = account.addresses[0];
      const country = countries.countries.find(
        (country) =>
          country.name === accountAddress.country ||
          country.isocode === accountAddress.country,
      );
      if (country) {
        billingAddress.country = {
          name: country.name,
          isocode: country.isocode,
        };
      }
      billingAddress.town = accountAddress.city;
      billingAddress.line1 = accountAddress.street;
      billingAddress.postalCode = accountAddress.zipcode;
      billingAddress.phone = phone;
    }

    return billingAddress;
  }
}
